import React, { useEffect, useMemo, useState } from 'react';
import ChangeIndicator from '@atlaskit/icon/glyph/hipchat/media-attachment-count';
import { Section } from '@atlaskit/side-navigation';
import { token } from '@atlaskit/tokens';
import Lozenge from '@atlaskit/lozenge';
import { Inline, Text } from '@atlaskit/primitives/compiled';
import { isFedRamp } from '@atlassian/atl-context';
import { SERVICE_DESK_PROJECT } from '@atlassian/jira-common-constants/src/project-types.tsx';
import { fg } from '@atlassian/jira-feature-gating';
import { useIntl } from '@atlassian/jira-intl';
import { PREMIUM_EDITION } from '@atlassian/jira-shared-types/src/edition.tsx';
import { ExpandableSubmenuItem } from '@atlassian/jira-navigation-apps-sidebar-common/src/common/ui/expandable-submenu-item/index.tsx';
import { MenuItem } from '@atlassian/jira-navigation-apps-sidebar-common/src/common/ui/menu-item/main.tsx';
import { TitleHeader } from '@atlassian/jira-navigation-apps-sidebar-common/src/common/ui/title-header/main.tsx';
import { useServiceDeskContextResource } from '@atlassian/jira-navigation-apps-sidebar-common/src/controllers/common/service-desk-context-resource/index.tsx';
import { useProject } from '@atlassian/jira-navigation-apps-sidebar-common/src/controllers/project/index.tsx';
import { NestingMenuItem } from '@atlassian/jira-navigation-apps-sidebar-common/src/ui/nesting-menu-item/main.tsx';
import { Skeleton } from '@atlassian/jira-navigation-apps-sidebar-common/src/ui/skeleton/index.tsx';
import { shouldRenderNesting } from '@atlassian/jira-navigation-apps-sidebar-common/src/utils/should-render/index.tsx';
import { useProjectSettingsForgeAppsForMenu } from '@atlassian/jira-navigation-apps-sidebar-forge/src/controllers/project-settings/index.tsx';
import { useCurrentRoute } from '@atlassian/jira-platform-router-utils/src/index.tsx';
import { useAppEditions } from '@atlassian/jira-tenant-context-controller/src/components/app-editions/index.tsx';
import { useAnalyticsEvents } from '@atlassian/jira-product-analytics-bridge';
import { getNextgenSettingsUrl } from '@atlassian/jira-project-settings-apps-common/src/urls.tsx';
import { useConnectApps } from '@atlassian/jira-project-settings-apps-connect-apps/src/baskets/connect-apps/index.tsx';
import type { App } from '@atlassian/jira-project-settings-apps-connect-apps/src/model/connect-apps/types.tsx';
import { getServiceDeskFeatures } from '@atlassian/jira-project-settings-product-features-jsm-tmp/src/index.tsx';
import { useTranslationSummaries } from '@atlassian/jira-servicedesk-languages/src/services/translation-summaries/index.tsx';
import { USER_JSM_PROJECT_ROLE } from '@atlassian/jira-servicedesk-spa-commons/src/common/utils/servicedesk-context/constants.tsx';
import { IssueTypesNavigationItemsFetcher } from '@atlassian/jira-spa-apps-project-settings-issue-type/src/view/team-managed/navigation/index.tsx';
import { useDlpEntitlement } from '@atlassian/jira-tenant-context-controller/src/components/dlp-entitlement/index.tsx';
import { useTenantContext } from '@atlassian/jira-tenant-context-controller/src/components/tenant-context/index.tsx';
import { matchPartialUrlPathname } from '@atlassian/jira-navigation-apps-sidebar-common/src/utils/url-matchers/index.tsx';
import { JourneyNavItemWrapper } from '@atlassian/jira-servicedesk-journeys/src/common/ui/journey-nav-item-wrapper/index.tsx';
import { useTMPSettingFeatures } from '../../../controllers/features/main.tsx';
import { SETTINGS_IDS, PROJECT_SETTINGS_SHIFTER_WEIGHT } from '../../constants.tsx';
import {
	JIRA_AUTOMATION_ADDON_KEY,
	JIRA_FEDRAMP_MODERATE_AUTOMATION_ADDON_KEY,
} from '../../utils/automation-paths/index.tsx';
import { filterServiceDeskConnectApps } from '../../utils/filtered-connect-apps/index.tsx';
import { AsyncAutomationChangeboarding } from '../automation-changeboarding/index.tsx';
import { Automation } from '../automation/main.tsx';
import ProjectSettingsChangeBoarding from '../settings-changeboarding/index.tsx';
import { AccessMenu } from './access/index.tsx';
import { Channels } from './channels/main.tsx';
import { EcosystemAppsMenu } from './ecosystem-apps/main.tsx';
import { Language } from './language/main.tsx';
import messages from './messages.tsx';
import { Notifications } from './notifications/main.tsx';
import { RequestsManagementMenu } from './request-management/index.tsx';
import type { SettingsNextgenProps } from './types.tsx';

const hasJiraAutomationConnectApp = (connectAppsData: App[] | null): boolean => {
	return (connectAppsData || []).some(({ connectIframeProps }) =>
		connectIframeProps
			? connectIframeProps.addon_key === JIRA_AUTOMATION_ADDON_KEY ||
				connectIframeProps.addon_key === JIRA_FEDRAMP_MODERATE_AUTOMATION_ADDON_KEY
			: false,
	);
};

/**
 * @deprecated This is a Nav3 component. If you do any changes here, please apply and QA them at Nav4 component as well: src/packages/navigation-apps/sidebar-nav4/project-settings/jsm-tmp-project-settings
 */
export const SettingsNextgenWrapped = ({ stack }: SettingsNextgenProps) => {
	const { formatMessage } = useIntl();
	const { data: project } = useProject();
	const route = useCurrentRoute();
	const tenantContext = useTenantContext();
	const { appPermissions } = tenantContext;
	const appEditions = useAppEditions();
	const hasDlpEnabled = useDlpEntitlement();
	const [{ data: translations }, { fetchTranslationSummaries }] = useTranslationSummaries();
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const [{ data: connectAppsData }, { fetch: fetchConnectApps }] = useConnectApps();
	const { userRole } = useServiceDeskContextResource().data || {};
	const { data: forgeSettingsItems } = useProjectSettingsForgeAppsForMenu();
	const { data: features } = useTMPSettingFeatures();

	const calculateAccessActive = ({ path }: { path: string }) =>
		/jira\/servicedesk\/projects\/.+\/settings\/customer-permissions/.test(path) ||
		/jira\/servicedesk\/projects\/.+\/settings\/access/.test(path);
	const [accessActive] = useState<boolean>(calculateAccessActive(route));

	const calculateRequestManagementActive = ({ path }: { path: string }) =>
		/jira\/servicedesk\/projects\/.+\/settings\/forms/.test(path) ||
		/jira\/servicedesk\/projects\/.+\/settings\/sla/.test(path) ||
		/jira\/servicedesk\/projects\/.+\/settings\/feedback-settings/.test(path) ||
		/jira\/servicedesk\/projects\/.+\/settings\/issuetypes\/.+/.test(path);

	const [requestManagementActive] = useState<boolean>(calculateRequestManagementActive(route));

	const calculateChannelsActive = ({ path }: { path: string }) =>
		/jira\/servicedesk\/projects\/.+\/settings\/channels\/portal-settings/.test(path) ||
		/jira\/servicedesk\/projects\/.+\/settings\/email/.test(path) ||
		/jira\/servicedesk\/projects\/.+\/settings\/widget/.test(path) ||
		/jira\/servicedesk\/projects\/.+\/settings\/chat/.test(path) ||
		/jira\/servicedesk\/projects\/.+\/settings\/confluence-kb/.test(path) ||
		/jira\/servicedesk\/projects\/.+\/settings\/virtual-agent/.test(path) ||
		/jira\/servicedesk\/projects\/.+\/settings\/external-links/.test(path);
	const [channelsActive] = useState<boolean>(calculateChannelsActive(route));

	const calculateNotificationsActive = ({ path }: { path: string }) =>
		/jira\/servicedesk\/projects\/.+\/settings\/customer-notifications/.test(path) ||
		/jira\/servicedesk\/projects\/.+\/settings\/internal-notifications/.test(path) ||
		/jira\/servicedesk\/projects\/.+\/settings\/notification-email/.test(path);
	const [notificationsActive] = useState<boolean>(calculateNotificationsActive(route));

	const calculateIntegrationsActive = ({ path }: { path: string }) =>
		/jira\/servicedesk\/projects\/.+\/settings\/apps\/.+/.test(path);
	const [integrationsActive] = useState<boolean>(calculateIntegrationsActive(route));

	useEffect(() => {
		if (project && userRole === USER_JSM_PROJECT_ROLE.AGENT_PROJECT_ADMIN_ROLE) {
			fetchTranslationSummaries(project.id, createAnalyticsEvent({}));
		}
	}, [createAnalyticsEvent, fetchTranslationSummaries, project, userRole]);

	// to remove on isAppControlsEnabled() cleanup
	useEffect(() => {
		if (project && !connectAppsData) fetchConnectApps(project.key);
	}, [connectAppsData, fetchConnectApps, project]);

	// This flag is for the case that when JSD is suspended/deactivated, only Details should be shown.
	const { hasServiceDeskAccess: hasJSD } = appPermissions;

	const hasPremiumServiceDeskLicense = appEditions.serviceDesk === PREMIUM_EDITION;

	const serviceDeskConnectApps = filterServiceDeskConnectApps(connectAppsData);

	const ecosystemApps = useMemo(
		() => ({
			forge: forgeSettingsItems,
			connect: serviceDeskConnectApps,
		}),
		[serviceDeskConnectApps, forgeSettingsItems],
	);

	if (project == null || features == null) return <Skeleton />;

	const { key: projectKey } = project;
	const baseSettingsUrl = getNextgenSettingsUrl(SERVICE_DESK_PROJECT, project.key);

	const BackMenuItem = {
		href: baseSettingsUrl,
		title: formatMessage(messages.goToSettings),
	};

	const productFeatures = getServiceDeskFeatures();

	return (
		<>
			<IssueTypesNavigationItemsFetcher projectId={project.id} productFeatures={productFeatures} />
			<TitleHeader>{formatMessage(messages.projectSettings)}</TitleHeader>
			<Section>
				<MenuItem
					analytics={{ itemId: 'details' }}
					selectedOn={['project-settings-servicedesk-details']}
					href={`${baseSettingsUrl}/details`}
					data-testId="navigation-apps-sidebar-servicedesk-common.ui.menu.settings-nextgen.details"
				>
					{formatMessage(messages.details)}
				</MenuItem>

				{hasJSD && (
					<MenuItem
						analytics={{ itemId: 'itsm-features' }}
						selectedOn={['project-settings-servicedesk-itsm-features']}
						href={`${baseSettingsUrl}/itsm-features`}
						shifter={{
							groupTitle: formatMessage(messages.title),
							context: projectKey,
							weight: PROJECT_SETTINGS_SHIFTER_WEIGHT,
						}}
					>
						{formatMessage(messages.itsmFeatures)}
					</MenuItem>
				)}

				<ExpandableSubmenuItem
					analytics={{ itemId: 'access-group' }}
					menuItemLabel={formatMessage(messages.accessGroup)}
					forceExpand={accessActive}
					submenuId="access-group-settings"
				>
					<AccessMenu projectKey={projectKey} features={features} />
				</ExpandableSubmenuItem>

				<ExpandableSubmenuItem
					analytics={{ itemId: 'requests-group' }}
					menuItemLabel={formatMessage(messages.requestsManagementGroup)}
					forceExpand={requestManagementActive}
					submenuId="requests-group-settings"
				>
					<RequestsManagementMenu projectKey={projectKey} />
				</ExpandableSubmenuItem>

				{hasJSD && (
					<ExpandableSubmenuItem
						analytics={{ itemId: 'channels-group' }}
						menuItemLabel={formatMessage(messages.channelsGroup)}
						forceExpand={channelsActive}
						submenuId="channels-group-settings"
					>
						<Channels projectKey={projectKey} features={features} />
					</ExpandableSubmenuItem>
				)}

				{hasJSD && (
					<ExpandableSubmenuItem
						analytics={{ itemId: 'notifications-group' }}
						menuItemLabel={formatMessage(messages.notificationsGroup)}
						forceExpand={notificationsActive}
						submenuId="notifications-group-settings"
					>
						<Notifications projectKey={projectKey} />
					</ExpandableSubmenuItem>
				)}

				{shouldRenderNesting(hasJSD, stack, 'languageSupport') && (
					<NestingMenuItem
						analytics={{ itemId: 'language-support' }}
						href={`${baseSettingsUrl}/language`}
						iconAfter={
							translations.isLanguageSupportHighlighted ? (
								// eslint-disable-next-line @atlaskit/design-system/no-legacy-icons
								<ChangeIndicator primaryColor={token('color.icon.warning')} label="" />
							) : null
						}
						id="languageSupport"
						overrides={{ BackMenuItem }}
						shifter={{
							groupTitle: formatMessage(messages.title),
							context: projectKey,
							weight: PROJECT_SETTINGS_SHIFTER_WEIGHT,
						}}
						title={formatMessage(messages.languageSupport)}
					>
						<Language />
					</NestingMenuItem>
				)}

				{features.hasJourneyBuilder && (
					<JourneyNavItemWrapper>
						<MenuItem
							analytics={{
								itemId: SETTINGS_IDS.JOURNEYS,
							}}
							href={`${baseSettingsUrl}/journeys`}
							selectedOn={matchPartialUrlPathname()}
						>
							<Inline as="span" space="space.100" alignBlock="center">
								<Text>{formatMessage(messages.journeys)}</Text>
								<Lozenge appearance="new">{formatMessage(messages.newLozenge)}</Lozenge>
							</Inline>
						</MenuItem>
					</JourneyNavItemWrapper>
				)}

				{hasJSD && (
					<>
						<Automation
							overrides={{ BackMenuItem }}
							shifter={{
								groupTitle: formatMessage(messages.title),
								context: projectKey,
								weight: PROJECT_SETTINGS_SHIFTER_WEIGHT,
							}}
							projectKey={projectKey}
							isSimplified
							hasJiraAutomation={hasJiraAutomationConnectApp(connectAppsData)}
						/>
						{(!isFedRamp() || !fg('playbooks_disable_for_fedramp')) &&
							hasPremiumServiceDeskLicense &&
							fg('playbooks-in-jsm') && (
								<MenuItem
									analytics={{ itemId: SETTINGS_IDS.PLAYBOOKS }}
									selectedOn={matchPartialUrlPathname()}
									href={`${baseSettingsUrl}/playbooks`}
									shifter={{
										groupTitle: formatMessage(messages.title),
										context: projectKey,
										weight: PROJECT_SETTINGS_SHIFTER_WEIGHT,
									}}
								>
									{formatMessage(messages.playbooks)}
								</MenuItem>
							)}
					</>
				)}

				{hasJSD && (
					<ExpandableSubmenuItem
						analytics={{ itemId: 'integrations-group' }}
						menuItemLabel={formatMessage(messages.integrationsGroup)}
						forceExpand={integrationsActive}
						submenuId="integrations-group-settings"
					>
						<EcosystemAppsMenu apps={ecosystemApps} />
					</ExpandableSubmenuItem>
				)}

				{route && !__SERVER__ && (
					<AsyncAutomationChangeboarding
						isLegacyAutomation={route.name.includes('project-settings-servicedesk-automation')}
					/>
				)}
				{hasDlpEnabled && (
					<MenuItem
						analytics={{ itemId: 'data-classifications' }}
						selectedOn={['project-settings-servicedesk-data-classifications']}
						href={`${baseSettingsUrl}/data-classifications`}
					>
						{formatMessage(messages.dataClassificationsNonFinal)}
					</MenuItem>
				)}
			</Section>
			<ProjectSettingsChangeBoarding />
		</>
	);
};

export const SettingsNextgen = ({ stack }: SettingsNextgenProps) => {
	const { data: project } = useProject();

	if (project == null) return <Skeleton />;

	return <SettingsNextgenWrapped stack={stack} />;
};
